import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import {
  CaasRoutes,
  CaasNavigationResponse,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { MenuItem } from '../../../hdi-components/src/shared/interfaces/caas/nav.interface';
import { environment } from '../environments/environment';
import { GlobalServices } from './global.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly apiKey = environment.navigationToken;
  private readonly caasUrl = environment.caasNavigationUrl;
  private readonly isPreview = environment.preview ? '@preview' : '';
  private cache: CaasRoutes[] | null = null;
  private specialRoutes = [
    'startseite',
    '404',
    '500',
    'metanavigation',
    'meta-navigation',
    'footer',
  ];

  private blacklistKeywords = ['w1', 'lux'];

  constructor(
    private http: HttpClient,
    private globalServices: GlobalServices
  ) {}

  /**
   * Fetches navigation data from the API.
   * @returns An observable of the navigation routes.
   */
  getNavigation(): Observable<CaasRoutes[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const url = this.buildUrl(this.isPreview);

    if (this.cache) {
      return of(this.cache);
    }
    return this.http.get<CaasNavigationResponse>(url, { headers }).pipe(
      retry(3),
      map((data: CaasNavigationResponse): CaasRoutes[] => {
        if (!data.navigation || data.navigation.length === 0) {
          return [];
        }
        return data.navigation || [];
      }),
      tap((data: CaasRoutes[]) => {
        this.cache = data;
      }),
      catchError((error) => {
        return this.globalServices.handleError(
          error,
          'NavigationService > getNavigation'
        );
      })
    );
  }

  private capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  /**
   * Generates breadcrumbs based on the navigation hierarchy and current path.
   * @param seoRoute The current route for which to generate breadcrumbs.
   * @returns An array of breadcrumb items.
   */
  generateBreadcrumbs(
    seoRoute: string,
    navigation: CaasRoutes[]
  ): { title: string; url: string }[] {
    const breadcrumbs: { title: string; url: string }[] = [];
    let currentPath = seoRoute.toLowerCase().replace(/^\/|\/$/g, '');

    // Helper function to recursively find the path in the navigation tree
    const findPath = (
      routes: CaasRoutes[],
      path: string
    ): CaasRoutes | null => {
      for (const route of routes) {
        const simplifiedPath = route.seoRoute
          .toLowerCase()
          .replace(/^\/|\/$/g, '');
        if (simplifiedPath === path) {
          return route;
        }
        if (route.children && route.children.length > 0) {
          const result = findPath(route.children, path);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };

    // Recursively generate breadcrumbs
    while (currentPath) {
      const matchingRoute = findPath(navigation, currentPath);
      if (matchingRoute) {
        breadcrumbs.unshift({
          title: matchingRoute.label || this.capitalize(currentPath),
          url: `/${currentPath}`,
        });
        currentPath = currentPath.split('/').slice(0, -1).join('/');
      } else {
        breadcrumbs.unshift({
          title: this.capitalize(
            seoRoute
              .trim()
              .toLowerCase()
              .replace(/^\/|\/$/g, '')
          ),
          url: seoRoute,
        });
        break;
      }
    }

    breadcrumbs.unshift({ title: 'Startseite', url: '/startseite' });
    return breadcrumbs;
  }

  /**
   * Retrieve the seoRoute based on the pageIdentifier.
   * @param pageIdentifier The identifier to look up.
   * @param navigation The navigation data to search through.
   * @returns The corresponding seoRoute.
   */
  getSeoRouteByIdentifier(
    pageIdentifier: string,
    navigation: CaasRoutes[]
  ): string | null {
    const findSeoRoute = (routes: CaasRoutes[], id: string): string | null => {
      for (const route of routes) {
        if (route.identifier === id) {
          return route.seoRoute;
        }

        // Recursively check children routes
        if (route.children && route.children.length > 0) {
          const found = findSeoRoute(route.children, id);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    return findSeoRoute(navigation, pageIdentifier);
  }

  /**
   * Transforms the navigation data into a format suitable for menu display.
   * @returns An observable of the main menu items.
   */
  getMainNavigation(): Observable<MenuItem[]> {
    return this.getNavigation().pipe(
      map((data) => {
        data = this.extractMainNavigation(data);
        return this.transformToMenuItems(data);
      })
    );
  }

  getMetaRoutes(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['metanavigation', 'meta-navigation'];
        const filteredMetaRoutes = this.filterKeywordsRoutes(data, keywords);
        return filteredMetaRoutes;
      })
    );
  }

  getFooterRoutes(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['footer'];
        const filteredFooterRoutes = this.filterKeywordsRoutes(data, keywords);
        return filteredFooterRoutes;
      })
    );
  }

  getNonSpecialRoutes(): Observable<CaasRoutes[]> {
    return this.getNavigation().pipe(
      map((routes) => routes.filter((route) => !this.isSpecialRoute(route)))
    );
  }

  private isSpecialRoute(route: CaasRoutes): boolean {
    return this.specialRoutes.some(
      (keyword) =>
        route.seoRoute.toLowerCase().includes(keyword) ||
        route.uid.toLowerCase().includes(keyword)
    );
  }

  getSpecialRoutes(): Observable<CaasRoutes[]> {
    return this.getNavigation().pipe(
      map((data: CaasRoutes[]) => {
        return this.filterKeywordsRoutes(data, this.specialRoutes) || [];
      })
    );
  }

  get404(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['404'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  get500(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['500'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  getHomePage(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['startseite', 'home'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  getFlyoutData(): Observable<
    { title: string; url: string; subpages: { title: string; url: string }[] }[]
  > {
    if (this.cache) {
      return of(this.buildFlyoutData(this.cache));
    }

    return this.getNavigation().pipe(
      map((data: CaasRoutes[]) => {
        const flyoutData = this.buildFlyoutData(data);
        return flyoutData;
      })
    );
  }

  private buildFlyoutData(items: CaasRoutes[]): {
    title: string;
    url: string;
    subpages: { title: string; url: string }[];
  }[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const filteredItems = items.filter(
      (item) => item.children && item.children.length
    );

    return filteredItems
      .map((item) => {
        const parentUrl = this.simplifyPath(
          item.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
        );

        const subpages = item.children
          .map((child) => {
            const childUrl = this.simplifyPath(
              child.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
            );
            return {
              title: child.label,
              url: childUrl,
            };
          })
          .filter(
            (subItem) =>
              subItem.title && subItem.url && subItem.url !== parentUrl
          );

        return {
          title: item.label,
          url: parentUrl,
          subpages,
        };
      })
      .filter((data) => {
        if (data.subpages.length === 0) {
          return false;
        }
        return true;
      });
  }

  private filterKeywordsRoutes(
    data: CaasRoutes[],
    keywords: string[]
  ): CaasRoutes[] {
    const regexPattern = new RegExp(keywords.join('|'), 'i');

    return data.filter((item) => {
      const seoRouteTrimmed = item.seoRoute.trim();
      const uidTrimmed = item.uid.trim();

      const matchesKeyword =
        regexPattern.test(seoRouteTrimmed) || regexPattern.test(uidTrimmed);

      // console.log(
      //   `filterKeywordsRoutes > Route: ${seoRouteTrimmed}, UID: ${uidTrimmed}, Matches Keyword: ${matchesKeyword}`
      // );

      return matchesKeyword;
    });
  }

  private simplifyPath(path: string) {
    const segments: string[] = path.split('/');
    const result: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      if (i === 0 || segments[i] !== segments[i - 1]) {
        result.push(segments[i]);
      }
    }

    return result.join('/');
  }

  private buildUrl(isPreview: string = 'release'): string {
    return `${this.caasUrl}${isPreview}/meta/struktur_de`;
  }

  private extractMainNavigation(data: CaasRoutes[]): CaasRoutes[] {
    if (!data || data.length === 0) {
      return [];
    }

    const mainNavigation = data.filter((item) => {
      const isBlacklisted =
        this.filterKeywordsRoutes([item], this.blacklistKeywords).length > 0;

      // console.log(`Route ${item.uid} (seoRoute: ${item.seoRoute}) ist blacklisted:`, isBlacklisted);

      return (
        item.children &&
        item.children.length > 0 &&
        item.showInNavigation &&
        !this.isSpecialRoute(item) &&
        !isBlacklisted
      );
    });

    return mainNavigation;
  }

  private transformToMenuItems(data: CaasRoutes[]): MenuItem[] {
    return data
      .filter((item) => {
        return !item.label.toLowerCase().includes('startseite');
      })
      .map((item) => {
        return {
          title: item.label,
          url: this.simplifyPath(
            item.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
          ),
          subitems: item.children
            .map((subItem) => ({
              title: subItem.label,
              url: this.simplifyPath(
                subItem.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
              ),
              subitems: subItem.children.map((subSubItem) => ({
                title: subSubItem.label,
                url: this.simplifyPath(
                  subSubItem.seoRoute.toLowerCase().replace(/^\/|\/$/g, '')
                ),
              })),
            }))
            .filter((subItem) => subItem.title !== item.label),
        };
      });
  }
}
